import axios from 'axios'
import { executeRecaptcha } from './recaptcha'

// Pull the API host from Vite env so dev / stage / prod hit the right
// backend subdomain. Falls back to same-origin (with a /api prefix) for
// the rare case where a reverse-proxy is set up at the same hostname.
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

// Attach token on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Handle 401 globally - clear token and redirect to login
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api

// ─── File download helper ─────────────────────────────────────────
// CSV/PDF endpoints return a binary body that must still carry the
// bearer token, so we fetch as a blob through `api` and then trigger
// a browser download. Filename comes from Content-Disposition when
// the server provides it.
export async function downloadFile(url, fallbackName) {
  const res = await api.get(url, { responseType: 'blob' })

  let filename = fallbackName
  const disposition = res.headers['content-disposition']
  if (disposition) {
    const match = /filename="?([^"]+)"?/.exec(disposition)
    if (match) filename = match[1]
  }

  const blobUrl = window.URL.createObjectURL(res.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(blobUrl)
}

// ─── Auth ─────────────────────────────────────────────────────────
// register + login auto-attach a v3 reCAPTCHA token; backend's
// enforceRecaptcha helper picks it up. Returns null when not configured.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action)
  return { ...data, recaptcha_token }
}
export const authApi = {
  register:       async (data) => api.post('/register', await withRecaptcha('register', data)),
  login:          async (data) => api.post('/login',    await withRecaptcha('login',    data)),
  // Email-OTP signup verification (added 2026-06-17).
  // verifyEmailOtp returns a sanctum token on success.
  // resendEmailOtp always succeeds (the server intentionally hides
  // whether the email matches a real account).
  verifyEmailOtp: (data) => api.post('/verify-email-otp', data),
  resendEmailOtp: (data) => api.post('/resend-email-otp', data),
  logout:         ()     => api.post('/logout'),
  me:             ()     => api.get('/me'),
  update:         (data) => api.patch('/me', data),
  changePassword: (data) => api.patch('/me/password', data),
}

// ─── Two-factor authentication ────────────────────────────────────
export const twoFactorApi = {
  status:        ()     => api.get('/me/2fa'),
  enable:        ()     => api.post('/me/2fa/enable'),
  confirm:       (data) => api.post('/me/2fa/confirm', data),
  disable:       (data) => api.delete('/me/2fa', { data }),
  recoveryCodes: (data) => api.post('/me/2fa/recovery-codes', data),
}

// ─── GDPR / CCPA data-subject rights ──────────────────────────────
export const privacyApi = {
  exportData:      ()     => downloadFile('/me/data-export', 'stovalt-export.zip'),
  deletionStatus:  ()     => api.get('/me/deletion-request'),
  requestDeletion: (data) => api.post('/me/deletion-request', data),
  cancelDeletion:  ()     => api.delete('/me/deletion-request'),
}

// ─── Dashboard ────────────────────────────────────────────────────
export const dashboardApi = {
  global:   ()           => api.get('/dashboard'),
  facility: (facilityId) => api.get(`/dashboard/facility/${facilityId}`),
}

// ─── Facilities ───────────────────────────────────────────────────
export const facilityApi = {
  list:   ()           => api.get('/facilities'),
  get:    (id)         => api.get(`/facilities/${id}`),
  create: (data)       => api.post('/facilities', data),
  update: (id, data)   => api.put(`/facilities/${id}`, data),
  delete: (id)         => api.delete(`/facilities/${id}`),
}

// ─── Unit Types ───────────────────────────────────────────────────
export const unitTypeApi = {
  list:   (fid)        => api.get(`/facilities/${fid}/unit-types`),
  get:    (fid, id)    => api.get(`/facilities/${fid}/unit-types/${id}`),
  create: (fid, data)  => api.post(`/facilities/${fid}/unit-types`, data),
  update: (fid, id, data) => api.put(`/facilities/${fid}/unit-types/${id}`, data),
  delete: (fid, id)    => api.delete(`/facilities/${fid}/unit-types/${id}`),
}

// ─── Units ────────────────────────────────────────────────────────
export const unitApi = {
  list:       (fid, params) => api.get(`/facilities/${fid}/units`, { params }),
  map:        (fid)         => api.get(`/facilities/${fid}/units-map`),
  get:        (fid, id)     => api.get(`/facilities/${fid}/units/${id}`),
  create:     (fid, data)   => api.post(`/facilities/${fid}/units`, data),
  bulkCreate: (fid, data)   => api.post(`/facilities/${fid}/units/bulk`, data),
  update:     (fid, id, data) => api.put(`/facilities/${fid}/units/${id}`, data),
  updateStatus: (id, data)  => api.patch(`/units/${id}/status`, data),
  delete:     (fid, id)     => api.delete(`/facilities/${fid}/units/${id}`),
}

// ─── Tenants ──────────────────────────────────────────────────────
export const tenantApi = {
  list:   (fid, params) => api.get(`/facilities/${fid}/tenants`, { params }),
  get:    (fid, id)     => api.get(`/facilities/${fid}/tenants/${id}`),
  create: (fid, data)   => api.post(`/facilities/${fid}/tenants`, data),
  update: (fid, id, data) => api.put(`/facilities/${fid}/tenants/${id}`, data),
  delete: (fid, id)     => api.delete(`/facilities/${fid}/tenants/${id}`),
  ledger: (fid, id)     => api.get(`/facilities/${fid}/tenants/${id}/ledger`),
}

// ─── Rentals ──────────────────────────────────────────────────────
export const rentalApi = {
  list:          (fid, params) => api.get(`/facilities/${fid}/rentals`, { params }),
  get:           (fid, id)     => api.get(`/facilities/${fid}/rentals/${id}`),
  create:        (fid, data)   => api.post(`/facilities/${fid}/rentals`, data),
  update:        (fid, id, data) => api.put(`/facilities/${fid}/rentals/${id}`, data),
  moveOut:       (id, data)    => api.post(`/rentals/${id}/move-out`, data),
  overlock:      (id)          => api.post(`/rentals/${id}/overlock`),
  removeOverlock:(id)          => api.post(`/rentals/${id}/remove-overlock`),
}

// ─── Invoices ─────────────────────────────────────────────────────
export const invoiceApi = {
  list:           (fid, params) => api.get(`/facilities/${fid}/invoices`, { params }),
  get:            (fid, id)     => api.get(`/facilities/${fid}/invoices/${id}`),
  create:         (fid, data)   => api.post(`/facilities/${fid}/invoices`, data),
  update:         (fid, id, data) => api.put(`/facilities/${fid}/invoices/${id}`, data),
  void:           (id)          => api.post(`/invoices/${id}/void`),
  applyLateFee:   (id)          => api.post(`/invoices/${id}/apply-late-fee`),
  generateMonthly:(fid)         => api.post(`/facilities/${fid}/invoices/generate-monthly`),
}

// ─── Payments ─────────────────────────────────────────────────────
export const paymentApi = {
  list:              (fid, params) => api.get(`/facilities/${fid}/payments`, { params }),
  get:               (id)          => api.get(`/payments/${id}`),
  recordManual:      (invoiceId, data) => api.post(`/invoices/${invoiceId}/payments`, data),
  createIntent:      (invoiceId)   => api.post(`/invoices/${invoiceId}/payment-intent`),
  refund:            (id)          => api.post(`/payments/${id}/refund`),
}

// ─── Access Codes ─────────────────────────────────────────────────
export const accessCodeApi = {
  list:       (fid, params) => api.get(`/facilities/${fid}/access-codes`, { params }),
  get:        (fid, id)     => api.get(`/facilities/${fid}/access-codes/${id}`),
  create:     (fid, data)   => api.post(`/facilities/${fid}/access-codes`, data),
  deactivate: (fid, id)     => api.post(`/facilities/${fid}/access-codes/${id}/deactivate`),
  activate:   (fid, id)     => api.post(`/facilities/${fid}/access-codes/${id}/activate`),
  extend:     (fid, id, data) => api.post(`/facilities/${fid}/access-codes/${id}/extend`, data),
  regenerate: (fid, id)     => api.post(`/facilities/${fid}/access-codes/${id}/regenerate`),
}

// ─── Team: Invitations (owner) ────────────────────────────────────
export const invitationApi = {
  list:   ()        => api.get('/team/invitations'),
  create: (data)    => api.post('/team/invitations', data),
  resend: (id)      => api.post(`/team/invitations/${id}/resend`),
  revoke: (id)      => api.delete(`/team/invitations/${id}`),
}

// ─── Team: Members (owner) ────────────────────────────────────────
export const teamApi = {
  list:   ()            => api.get('/team/members'),
  update: (userId, data) => api.patch(`/team/members/${userId}`, data),
  remove: (userId)      => api.delete(`/team/members/${userId}`),
}

// ─── Public booking funnel (unauthenticated) ─────────────────────
// Note: this calls /api/public/* - no token needed; rate-limited server-side.
const publicApi = axios.create({
  baseURL: `${API_BASE}/api/public`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

export const publicBookingApi = {
  facilities:        ()           => publicApi.get('/facilities'),
  facility:          (slug)       => publicApi.get(`/facilities/${slug}`),
  unitType:          (slug, utId) => publicApi.get(`/facilities/${slug}/unit-types/${utId}`),
  startBooking:      (data)       => publicApi.post('/bookings', data),
  getBooking:        (token)      => publicApi.get(`/bookings/${token}`),
  updateBooking:     (token, data) => publicApi.patch(`/bookings/${token}`, data),
  checkout:          (token, data) => publicApi.post(`/bookings/${token}/checkout`, data),
  complete:          (token, data) => publicApi.post(`/bookings/${token}/complete`, data),
  completeBooking:   (token, data) => publicApi.post(`/bookings/${token}/complete`, data), // legacy alias
}

// ─── Public invite acceptance ────────────────────────────────────
// These do NOT go through the auth interceptor for an unauthenticated
// invitee - but an existing user's sanctum token in localStorage will
// still be sent automatically, which is what the accept endpoint expects
// for the "existing user" branch.
export const invitePublicApi = {
  show:   (token)        => api.get(`/invitations/${token}`),
  accept: (token, data)  => api.post(`/invitations/${token}/accept`, data),
}

// ─── Audit log (Activity) ─────────────────────────────────────────
export const auditApi = {
  facility: (fid, params) => api.get(`/facilities/${fid}/audit-logs`, { params }),
  all:      (params)      => api.get('/audit-logs', { params }),
}

// ─── Move-out requests (operator side) ────────────────────────────
export const moveOutRequestsApi = {
  list:    (fid, params) => api.get(`/facilities/${fid}/move-out-requests`, { params }),
  accept:  (id, data)    => api.post(`/move-out-requests/${id}/accept`, data),
  decline: (id, data)    => api.post(`/move-out-requests/${id}/decline`, data),
}

// ─── Wave 3.1 Reports ─────────────────────────────────────────────
export const reportsApi = {
  summary:         (fid)        => api.get(`/facilities/${fid}/reports/summary`),
  aging:           (fid)        => api.get(`/facilities/${fid}/reports/aging`),
  dso:             (fid, days)  => api.get(`/facilities/${fid}/reports/dso`, { params: { days } }),
  occupancy:       (fid)        => api.get(`/facilities/${fid}/reports/occupancy`),
  revenueForecast: (fid, months) => api.get(`/facilities/${fid}/reports/revenue-forecast`, { params: { months } }),
  moveInsOuts:     (fid)        => api.get(`/facilities/${fid}/reports/move-ins-outs`),
  tax:             (fid, year)  => api.get(`/facilities/${fid}/reports/tax`, { params: { year } }),
  download:        (fid, type, format = 'csv') =>
    downloadFile(`/facilities/${fid}/reports/${type}/download?format=${format}`, `${type}.${format}`),
}

// Lien notice PDFs
export const lienNoticeApi = {
  pdf: (id) => downloadFile(`/lien-notices/${id}/pdf`, `lien-notice-${id}.pdf`),
}

// Auction listing PDF (bidder catalog)
export const auctionApi = {
  listingPdf: (fid, date) => downloadFile(
    `/facilities/${fid}/auctions/listing-pdf${date ? `?date=${date}` : ''}`,
    `auction-listing.pdf`,
  ),
}

export const scheduledReportsApi = {
  list:   (fid)       => api.get(`/facilities/${fid}/scheduled-reports`),
  create: (fid, data) => api.post(`/facilities/${fid}/scheduled-reports`, data),
  update: (id, data)  => api.patch(`/scheduled-reports/${id}`, data),
  remove: (id)        => api.delete(`/scheduled-reports/${id}`),
}

// ─── Wave 3.3 Lien notices + Auctions ─────────────────────────────
export const lienNoticesApi = {
  list:   (fid, params) => api.get(`/facilities/${fid}/lien-notices`, { params }),
  create: (fid, data)   => api.post(`/facilities/${fid}/lien-notices`, data),
  update: (id, data)    => api.patch(`/lien-notices/${id}`, data),
  remove: (id)          => api.delete(`/lien-notices/${id}`),
}

export const auctionsApi = {
  list:     (fid, params) => api.get(`/facilities/${fid}/auctions`, { params }),
  create:   (fid, data)   => api.post(`/facilities/${fid}/auctions`, data),
  complete: (id, data)    => api.post(`/auctions/${id}/complete`, data),
  cancel:   (id, reason)  => api.post(`/auctions/${id}/cancel`, { reason }),
}

// ─── Wave 3.2 Accounting integrations ─────────────────────────────
export const accountingApi = {
  status:     (fid)             => api.get(`/facilities/${fid}/integrations/accounting`),
  connect:    (fid, provider)   => api.post(`/facilities/${fid}/integrations/accounting/${provider}/connect`),
  disconnect: (fid, provider)   => api.post(`/facilities/${fid}/integrations/accounting/${provider}/disconnect`),
  sync:       (fid, provider)   => api.post(`/facilities/${fid}/integrations/accounting/${provider}/sync`),
}

// ─── Exports (CSV + PDF downloads) ────────────────────────────────
export const exportApi = {
  tenants:    (fid) => downloadFile(`/facilities/${fid}/exports/tenants`,   'tenants.csv'),
  rentals:    (fid) => downloadFile(`/facilities/${fid}/exports/rentals`,   'rentals.csv'),
  invoices:   (fid) => downloadFile(`/facilities/${fid}/exports/invoices`,  'invoices.csv'),
  payments:   (fid) => downloadFile(`/facilities/${fid}/exports/payments`,  'payments.csv'),
  rentRoll:   (fid) => downloadFile(`/facilities/${fid}/exports/rent-roll`, 'rent-roll.pdf'),
  invoicePdf: (id)  => downloadFile(`/invoices/${id}/pdf`,                  `invoice-${id}.pdf`),
}

// ─── Billing (operator subscription) ──────────────────────────────
// checkout() returns { checkout_url, swapped }. A null checkout_url with
// swapped:true means the operator already had a subscription and the plan
// was changed in place - no redirect needed, just refetch.
export const billingApi = {
  get:       ()      => api.get('/billing'),
  checkout:  (plan)  => api.post('/billing/checkout', { plan }),
  portal:    ()      => api.post('/billing/portal'),
  sync:      ()      => api.post('/billing/sync'),
  cancel:    ()      => api.delete('/billing/subscription'),
  resume:    ()      => api.post('/billing/subscription/resume'),
}

// ─── Platform Settings (super-admin only) ─────────────────────────
export const platformSettingsApi = {
  list:   ()     => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', { settings: data }),
}
